import React from 'react';
import { ArrowUpRight, Phone, Mail, MapPin } from 'lucide-react';
import { contactInfo } from '../mock';
import useReveal from '../hooks/useReveal';

const Contact = () => {
  const ref = useReveal({ threshold: 0.1 });

  return (
    <section id="contact" ref={ref} className="relative py-16 md:py-20 px-5 sm:px-6 md:px-10 border-t border-white/5 overflow-hidden">
      {/* At-the-doorstep background — Ionic team meeting a client */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <img
          src="/contact-handshake.webp" srcSet="/contact-handshake-400w.webp 400w, /contact-handshake-800w.webp 800w, /contact-handshake.webp 2000w" sizes="(max-width: 640px) 400px, (max-width: 1024px) 800px, 2000px" loading="lazy" decoding="async"
          alt=""
          className="w-full h-full object-cover object-center opacity-[0.42]"
        />
        <div className="absolute inset-0 bg-[color:var(--obsidian)]/55" />
        <div className="absolute inset-0 bg-gradient-to-b from-[color:var(--obsidian)] via-transparent to-[color:var(--obsidian)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(10,10,10,0.4)_70%,rgba(10,10,10,0.9)_100%)]" />
      </div>

      <div className="relative max-w-[1400px] mx-auto grid lg:grid-cols-[.55fr_.45fr] gap-10 lg:gap-14">
        <div className="min-w-0">
          <div className="reveal font-mono text-[12px] md:text-[10.5px] tracking-[0.24em] uppercase text-[color:var(--green)] mb-4">&mdash; Contact</div>
          <h2 className="reveal font-display text-[32px] sm:text-[42px] md:text-[64px] lg:text-[76px] leading-[1.02] text-[color:var(--parchment)]">
            Speak with an <span className="text-[color:var(--green)]">Ionic</span> specialist.
          </h2>
          <p className="reveal mt-6 text-[15px] md:text-base text-[color:var(--parchment)]/70 leading-relaxed max-w-lg">
            Whether you&rsquo;re a family settling an estate, an executor, a senior downsizing, or a real estate or legal professional &mdash; a specialist is a call away. Every consultation is private, complimentary, and pressure-free.
          </p>

          <div className="reveal mt-10 md:mt-12 flex flex-wrap gap-2">
            {contactInfo.serviceAreas.map((c) => (
              <span key={c} className="font-mono text-[12px] md:text-[10.5px] tracking-[0.22em] uppercase px-3 py-1 rounded-full border border-white/15 text-[color:var(--parchment)]/70">
                {c}
              </span>
            ))}
          </div>
        </div>

        <div className="reveal border border-white/10 rounded-[6px] divide-y divide-white/10 min-w-0">
          <a href={contactInfo.phoneHref} className="group flex items-center justify-between gap-3 p-5 sm:p-6 md:p-8 hover:bg-[color:var(--obsidian-2)]/60 transition-colors">
            <div className="flex items-center gap-4 sm:gap-5 min-w-0 flex-1">
              <div className="shrink-0 w-11 h-11 sm:w-12 sm:h-12 rounded-full border border-white/15 flex items-center justify-center text-[color:var(--parchment)] group-hover:border-[color:var(--green)] group-hover:text-[color:var(--green)] transition-colors">
                <Phone className="w-4 h-4 sm:w-5 sm:h-5" />
              </div>
              <div className="min-w-0">
                <div className="font-mono text-[12px] md:text-[10px] tracking-[0.24em] uppercase text-[color:var(--parchment)]/55">Southern Oregon Division</div>
                <div className="font-display text-xl sm:text-2xl md:text-3xl text-[color:var(--parchment)] group-hover:text-[color:var(--green)] transition-colors">{contactInfo.phone}</div>
              </div>
            </div>
            <ArrowUpRight className="shrink-0 w-5 h-5 text-[color:var(--parchment)]/60 group-hover:text-[color:var(--green)] transition-colors" />
          </a>

          <a href={contactInfo.emailHref} data-testid="contact-email-link" className="group flex items-center justify-between gap-3 p-5 sm:p-6 md:p-8 hover:bg-[color:var(--obsidian-2)]/60 transition-colors">
            <div className="flex items-center gap-4 sm:gap-5 min-w-0 flex-1">
              <div className="shrink-0 w-11 h-11 sm:w-12 sm:h-12 rounded-full border border-white/15 flex items-center justify-center text-[color:var(--parchment)] group-hover:border-[color:var(--green)] group-hover:text-[color:var(--green)] transition-colors">
                <Mail className="w-4 h-4 sm:w-5 sm:h-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-mono text-[12px] md:text-[10px] tracking-[0.24em] uppercase text-[color:var(--parchment)]/55">Drop us a line</div>
                <div className="font-display text-base sm:text-lg md:text-xl text-[color:var(--parchment)] break-all sm:break-normal group-hover:text-[color:var(--green)] transition-colors">{contactInfo.emailDisplay}</div>
              </div>
            </div>
            <ArrowUpRight className="shrink-0 w-5 h-5 text-[color:var(--parchment)]/60 group-hover:text-[color:var(--green)] transition-colors" />
          </a>

          <div className="flex items-center justify-between gap-3 p-5 sm:p-6 md:p-8">
            <div className="flex items-center gap-4 sm:gap-5 min-w-0 flex-1">
              <div className="shrink-0 w-11 h-11 sm:w-12 sm:h-12 rounded-full border border-white/15 flex items-center justify-center text-[color:var(--parchment)]">
                <MapPin className="w-4 h-4 sm:w-5 sm:h-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-mono text-[12px] md:text-[10px] tracking-[0.24em] uppercase text-[color:var(--parchment)]/55">Serving</div>
                <div className="font-display text-base sm:text-lg md:text-xl text-[color:var(--parchment)] leading-tight">
                  Southern Oregon &middot; Portland &middot; Vancouver
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
